import { buildApiUrl } from '../config/api';
import { decryptJsonBody, encryptJsonBody, isEncryptedEnvelope, isEncryptionEnabled } from '../utils/cryptoTransport';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type QueryValue = string | number | boolean | undefined | null;

interface RequestOptions {
  headers?: HeadersInit;
  params?: Record<string, QueryValue>;
}

export class ApiError extends Error {
  statusCode: number;
  responseBody: unknown;

  constructor(message: string, statusCode: number, responseBody: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.responseBody = responseBody;
  }
}

const getStoredUser = (): Record<string, unknown> | null => {
  const rawUser = localStorage.getItem('authUser') || localStorage.getItem('loginUser');

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const buildQueryString = (params?: Record<string, QueryValue>): string => {
  if (!params) {
    return '';
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const getDefaultHeaders = (): Headers => {
  const headers = new Headers();
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  const user = getStoredUser();

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (user) {
    const userId = user._id;
    const userRole = user.role_name;
    const userName = user.name;

    if (typeof userId === 'string' && userId) {
      headers.set('User-ID', userId);
    }

    if (typeof userRole === 'string' && userRole) {
      headers.set('User-Role', userRole);
    }

    if (typeof userName === 'string' && userName) {
      headers.set('User-Name', userName);
    }
  }

  return headers;
};

const request = async <TResponse>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<TResponse> => {
  const defaultHeaders = getDefaultHeaders();
  const customHeaders = options?.headers ? new Headers(options.headers) : null;

  if (customHeaders) {
    customHeaders.forEach((value, key) => {
      defaultHeaders.set(key, value);
    });
  }

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const shouldEncrypt = isEncryptionEnabled() && !isFormData && body !== undefined;

  if (!isFormData && body !== undefined && !defaultHeaders.has('Content-Type')) {
    defaultHeaders.set('Content-Type', 'application/json');
  }

  if (shouldEncrypt) {
    defaultHeaders.set('X-Payload-Encrypted', 'aes-256-gcm');
  }

  const requestBody = isFormData
    ? (body as FormData)
    : body === undefined
      ? undefined
      : JSON.stringify(shouldEncrypt ? await encryptJsonBody(body) : body);

  const url = `${buildApiUrl(path)}${buildQueryString(options?.params)}`;
  const response = await fetch(url, {
    method,
    headers: defaultHeaders,
    body: requestBody,
  });

  let responseBody: unknown = null;

  try {
    responseBody = await response.json();
  } catch {
    responseBody = null;
  }

  if (isEncryptionEnabled() && isEncryptedEnvelope(responseBody)) {
    responseBody = await decryptJsonBody(responseBody);
  }

  if (!response.ok) {
    const message =
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'message' in responseBody &&
      typeof (responseBody as { message?: unknown }).message === 'string'
        ? ((responseBody as { message: string }).message)
        : `Request failed with status ${response.status}`;

    throw new ApiError(message, response.status, responseBody);
  }

  return responseBody as TResponse;
};

const requestBinary = async (
  method: HttpMethod,
  path: string,
  options?: RequestOptions
): Promise<Blob> => {
  const defaultHeaders = getDefaultHeaders();
  const customHeaders = options?.headers ? new Headers(options.headers) : null;

  if (customHeaders) {
    customHeaders.forEach((value, key) => {
      defaultHeaders.set(key, value);
    });
  }

  const url = `${buildApiUrl(path)}${buildQueryString(options?.params)}`;
  const response = await fetch(url, {
    method,
    headers: defaultHeaders,
  });

  if (!response.ok) {
    let responseBody: unknown = null;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }

    const message =
      typeof responseBody === 'object' &&
      responseBody !== null &&
      'message' in responseBody &&
      typeof (responseBody as { message?: unknown }).message === 'string'
        ? (responseBody as { message: string }).message
        : `Request failed with status ${response.status}`;

    throw new ApiError(message, response.status, responseBody);
  }

  return response.blob();
};

const api = {
  get: <TResponse>(path: string, options?: RequestOptions): Promise<TResponse> => request<TResponse>('GET', path, undefined, options),
  post: <TResponse, TPayload = unknown>(path: string, payload: TPayload, options?: RequestOptions): Promise<TResponse> =>
    request<TResponse>('POST', path, payload, options),
  put: <TResponse, TPayload = unknown>(path: string, payload: TPayload, options?: RequestOptions): Promise<TResponse> =>
    request<TResponse>('PUT', path, payload, options),
  patch: <TResponse, TPayload = unknown>(path: string, payload: TPayload, options?: RequestOptions): Promise<TResponse> =>
    request<TResponse>('PATCH', path, payload, options),
  delete: <TResponse, TPayload = unknown>(path: string, payload?: TPayload, options?: RequestOptions): Promise<TResponse> =>
    request<TResponse>('DELETE', path, payload, options),
  download: (path: string, options?: RequestOptions): Promise<Blob> => requestBinary('GET', path, options),
};

export default api;
